import * as React from "react";
import { enforceNewActionFeatures } from "components/ActionPropertiesEditor/enforceNewActionFeatures";
import { ActionPlugin } from "components/Actions/pluginRegistry";
import { ActionPropertiesEditor } from "components/ActionPropertiesEditor/ActionPropertiesEditor";
import ActionProperties from "client/resources/actionProperties";
import { PackageReference } from "client/resources/packageReference";
import { Errors } from "components/DataBaseComponent";
import { ActionTemplateParameterResource } from "client/resources";
import { ActionTemplatePackageSelectorDependencies } from "components/StepPackageEditor/Inputs/Components/PackageSelector/PackageSelectorDependencies";
import { useCallback, useEffect, useMemo } from "react";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import { useKeyedItemAccess } from "components/KeyAccessProvider/KeyedItemAccessProvider";
import { useScriptActionContext } from "components/Actions/script/ScriptActionContext";

interface ActionTemplateEditorInternalProps {
    localNames: string[];
    plugin: ActionPlugin;
    properties: ActionProperties;
    packages: Array<PackageReference>;
    errors: Errors | undefined; // only used for shouldComponentUpdate
    busy: Promise<unknown> | boolean | undefined;
    expandedByDefault: boolean;
    parameters: ActionTemplateParameterResource[];
    getFieldError(field: string): string;
    setProperties(properties: Partial<ActionProperties>, initialise?: boolean, callback?: () => void): void;
    setPackages(packages: Array<PackageReference>, initialise?: boolean): void;
    doBusyTask(action: () => Promise<void>): Promise<boolean>;
    isNew: boolean;
}

export function ActionTemplatePropertiesEditor(props: ActionTemplateEditorInternalProps) {
    const { packages, setPackages, parameters, localNames } = props;
    useEffect(() => {
        const properties = { ...props.properties };

        const enabledFeatures = enforceNewActionFeatures(props.plugin, props.properties, props.isNew);
        if (!!enabledFeatures) {
            // Server will strip empty properties, so only set this if we need to, otherwise this triggers our save-changes dialog unnecessarily.
            properties["Octopus.Action.EnabledFeatures"] = enforceNewActionFeatures(props.plugin, props.properties, props.isNew);
        }

        props.setProperties(properties, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const itemKey = useKeyedItemAccess();
    const stableSetPackages = useCallback((getUpdatedPackages: (prev: Array<PackageReference>) => Array<PackageReference>) => setPackages(getUpdatedPackages(packages)), [packages, setPackages]);
    const stableRefreshFeeds = useCallback(async () => {
        await refreshFeeds();
    }, [refreshFeeds]);
    const dependencies = useMemo<ActionTemplatePackageSelectorDependencies>(
        () => ({
            feeds: feeds,
            refreshFeeds: stableRefreshFeeds,
            parameters: parameters,
            setPackages: stableSetPackages,
            localNames: localNames,
            itemKey,
        }),
        [feeds, itemKey, localNames, parameters, stableRefreshFeeds, stableSetPackages]
    );

    if (!props.plugin.edit) {
        throw new Error(`Plugin ${props.plugin.actionType} is missing 'Edit' property`);
    }

    return (
        <ActionPropertiesEditor
            packageSelectorDependencies={dependencies}
            plugin={props.plugin}
            properties={props.properties}
            packages={packages}
            doBusyTask={props.doBusyTask}
            busy={props.busy}
            setProperties={props.setProperties}
            setPackages={setPackages}
            getFieldError={props.getFieldError}
            errors={props.errors}
            expandedByDefault={props.expandedByDefault}
        />
    );
}
